// ═══════════════════════════════════════════════════════════════
// ERROR BOUNDARY
// Catches render errors anywhere in the tree and renders a
// visible diagnostic panel instead of a silent white screen.
//
// Without this, any uncaught error in a provider or lazy-loaded
// module unmounts the entire React tree — the DOM stays white
// and the user has no idea what happened. With this, they see
// the error message, stack trace, and a reload button.
// ═══════════════════════════════════════════════════════════════

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '../../utils/lazyWithReload';
import { CHUNK_RELOAD_GUARD_KEY } from '../../utils/version';

interface Props {
  children: ReactNode;
  /** Optional label shown above the error panel to help locate where it fired. */
  scope?: string;
  /** Optional custom fallback UI; defaults to the built-in diagnostic panel. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Defense-in-depth: a ChunkLoadError from any lazy import not wrapped by
    // lazyWithReload (e.g. a nested lazy) self-heals with a single reload.
    // Shares the one-shot sessionStorage guard with lazyWithReload, so the
    // two layers can never reload more than once between good loads.
    if (isChunkLoadError(error)) {
      let reloaded = false;
      try { reloaded = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1'; } catch { /* ignore */ }
      if (!reloaded) {
        try { sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1'); } catch { /* ignore */ }
        window.location.reload();
      }
    }
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console so the user can grab it from DevTools.
    console.error('[ErrorBoundary] Render error caught:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    const { error, errorInfo } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-3xl w-full bg-white border border-red-200 rounded-lg shadow-lg overflow-hidden">
          <div className="bg-red-50 border-b border-red-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 2l10 18H2L12 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-red-900">
                  Something crashed
                  {this.props.scope ? ` in ${this.props.scope}` : ''}
                </h1>
                <p className="text-sm text-red-700">
                  The app caught a render error. Details below — screenshot or copy this for debugging.
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Error</div>
              <div className="font-mono text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2 break-all">
                {error.name}: {error.message}
              </div>
            </div>

            {error.stack && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Stack</div>
                <pre className="font-mono text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                  {error.stack}
                </pre>
              </div>
            )}

            {errorInfo?.componentStack && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Component tree</div>
                <pre className="font-mono text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                  {errorInfo.componentStack}
                </pre>
              </div>
            )}
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Try to recover
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded hover:bg-red-700"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
